import { useCallback, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Activity, Download, Search, X } from 'lucide-react-native'
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Avatar, Button, InlineAlert, TextField } from '@chillout/ui'
import { subDays, startOfDay, endOfDay, format } from 'date-fns'
import { AdminScreen } from '../AdminScreen'
import { AdminTable } from '../shared/Table'
import { AdminFilterDropdown, type FilterGroup } from '../shared/AdminFilterDropdown'
import { DateRangePicker } from '../auditLog/DateRangePicker'
import { Card } from '../../shared/Card'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { RecentActivityDetailModal } from './RecentActivityDetailModal'
import { useRecentActivity } from './useRecentActivity'
import { useActorList } from './useActorList'
import { exportRecentActivityCsv, type ExportFilterLabels } from './exportRecentActivityCsv'
import { PAGE_SIZE, ACTIVITY_TYPES, type RecentActivityEntry } from './recentActivityConstants'

function formatDateTime(createdAt: string, t: (key: string) => string, language: string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()
  const dayLabel = isToday
    ? t('common.today')
    : isYesterday
      ? t('common.yesterday')
      : date.toLocaleDateString(language, { day: 'numeric', month: 'short' })
  const timeLabel = date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }).replace(/ /g, ' ')
  return `${dayLabel}, ${timeLabel}`
}

function formatRelativeTime(createdAt: string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 4) return `${diffWeeks}w ago`
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

function getActivityLabel(type: string, t: (key: string) => string): string {
  if (type === 'new_member_joined') return t('recentActivity.activityTypes.new_member_joined')
  return t(`common.activity.type.${type}` as never)
}

function formatMobileDelta(entry: RecentActivityEntry, t: (key: string) => string): { label: string; isNegative: boolean } | null {
  if (entry.stampsDelta !== 0) {
    const sign = entry.stampsDelta > 0 ? '+' : ''
    const word = Math.abs(entry.stampsDelta) === 1 ? t('common.activity.stampSingle') : t('common.activity.stamps')
    return { label: `${sign}${entry.stampsDelta} ${word}`, isNegative: entry.stampsDelta < 0 }
  }
  if (entry.rewardsDelta !== 0) {
    const sign = entry.rewardsDelta > 0 ? '+' : ''
    const word = Math.abs(entry.rewardsDelta) === 1 ? t('common.activity.rewardSingle') : t('common.activity.rewards')
    return { label: `${sign}${entry.rewardsDelta} ${word}`, isNegative: entry.rewardsDelta < 0 }
  }
  return null
}

function formatActorColumn(name: string | null, role: string | null, t: (key: string) => string): string {
  if (!name || role === 'system') return t('recentActivity.detail.system')
  if (role === 'manager') return `${name} (${t('recentActivity.detail.managerRole')})`
  if (role === 'admin') return `${name} (${t('recentActivity.detail.adminRole')})`
  return name
}

interface RecentActivityScreenProps {
  initialMemberQuery?: string
}

export function RecentActivityScreen({ initialMemberQuery }: RecentActivityScreenProps) {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState(initialMemberQuery ?? '')
  const [activeFilters, setActiveFilters] = useState<Record<string, string | string[] | undefined>>({})
  const [selectedEntry, setSelectedEntry] = useState<RecentActivityEntry | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  const [dateFrom, setDateFrom] = useState(() => startOfDay(subDays(new Date(), 6)))
  const [dateTo, setDateTo] = useState(() => endOfDay(new Date()))

  const debouncedQuery = useDebouncedValue(searchQuery, 300)

  const { data: actorList = [] } = useActorList()

  const activityTypes = Array.isArray(activeFilters.activityType) ? activeFilters.activityType : undefined
  const actorIds = Array.isArray(activeFilters.actorId) ? activeFilters.actorId : undefined
  const anonymisedFilter = Array.isArray(activeFilters.memberStatus)
    ? activeFilters.memberStatus
    : undefined

  const activityQuery = useRecentActivity(
    {
      page,
      dateFrom,
      dateTo,
      memberQuery: debouncedQuery || undefined,
      activityTypes,
      actorIds,
      isAnonymised: anonymisedFilter?.length === 1
        ? anonymisedFilter[0] === 'anonymised'
        : undefined,
    },
    i18n.language,
  )

  const entries = useMemo(() => activityQuery.data?.entries ?? [], [activityQuery.data])
  const totalCount = activityQuery.data?.totalCount ?? 0
  const pageCount = Math.ceil(totalCount / PAGE_SIZE)

  const filterGroups = useMemo<FilterGroup[]>(
    () => [
      {
        id: 'activityType',
        label: t('recentActivity.filters.activityType'),
        multiSelect: true,
        options: ACTIVITY_TYPES.map((type) => ({
          label: type === 'new_member_joined'
            ? t('recentActivity.activityTypes.new_member_joined')
            : t(`common.activity.type.${type}` as never),
          value: type,
        })),
      },
      ...(actorList.length > 0
        ? [{
            id: 'actorId',
            label: t('recentActivity.filters.staff'),
            multiSelect: true,
            options: actorList.map((actor) => ({
              label: actor.role === 'manager'
                ? `${actor.name} (${t('recentActivity.detail.managerRole')})`
                : actor.role === 'admin'
                  ? `${actor.name} (${t('recentActivity.detail.adminRole')})`
                  : actor.name,
              value: actor.id,
            })),
          }]
        : []),
      {
        id: 'memberStatus',
        label: t('recentActivity.filters.memberStatus'),
        multiSelect: true,
        options: [
          { label: t('recentActivity.filters.anonymised'), value: 'anonymised' },
          { label: t('recentActivity.filters.active'), value: 'active' },
        ],
      },
    ],
    [t, actorList],
  )

  const columns = useMemo<ColumnDef<RecentActivityEntry>[]>(
    () => [
      {
        id: 'dateTime',
        header: t('recentActivity.columns.dateTime'),
        size: 90,
        cell: ({ row }) => {
          const formatted = formatDateTime(row.original.createdAt, t, i18n.language)
          const parts = formatted.split(', ')
          return (
            <Text className="text-sm font-normal text-primary">
              {parts[0]}, <Text className="text-secondary">{parts[1]}</Text>
            </Text>
          )
        },
      },
      {
        id: 'activity',
        header: t('recentActivity.columns.activity'),
        size: 150,
        cell: ({ row }) => (
          <Text className="text-sm font-normal text-primary">
            {getActivityLabel(row.original.type, t)}
          </Text>
        ),
      },
      {
        id: 'change',
        header: t('recentActivity.columns.change'),
        size: 80,
        cell: ({ row }) => {
          const { stampsDelta, rewardsDelta, rewardItemName } = row.original
          if (stampsDelta !== 0) {
            const sign = stampsDelta > 0 ? '+' : ''
            const word = Math.abs(stampsDelta) === 1 ? t('common.activity.stampSingle') : t('common.activity.stamps')
            return (
              <Text className={`text-sm font-medium ${stampsDelta < 0 ? 'text-accent' : 'text-primary'}`}>
                {sign}{stampsDelta} {word}
              </Text>
            )
          }
          if (rewardsDelta !== 0) {
            const sign = rewardsDelta > 0 ? '+' : ''
            const word = Math.abs(rewardsDelta) === 1 ? t('common.activity.rewardSingle') : t('common.activity.rewards')
            return (
              <View>
                <Text className={`text-sm font-medium ${rewardsDelta < 0 ? 'text-accent' : 'text-primary'}`}>
                  {sign}{rewardsDelta} {word}
                </Text>
                {rewardItemName ? (
                  <Text className="text-xs font-normal text-secondary" numberOfLines={1}>{rewardItemName}</Text>
                ) : null}
              </View>
            )
          }
          return <Text className="text-sm font-normal text-muted">—</Text>
        },
      },
      {
        id: 'member',
        header: t('recentActivity.columns.member'),
        size: 180,
        cell: ({ row }) => (
          <View className="flex-row items-center gap-3">
            <Avatar name={row.original.memberName} email={row.original.memberEmail} size={32} />
            <View className="min-w-0 flex-1">
              <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
                {row.original.memberName}
              </Text>
              <Text className="text-xs font-normal text-secondary" numberOfLines={1}>
                {row.original.memberEmail}
              </Text>
            </View>
          </View>
        ),
      },
      {
        id: 'by',
        header: t('recentActivity.columns.by'),
        size: 100,
        cell: ({ row }) => (
          <Text className="text-sm font-normal text-primary" numberOfLines={1}>
            {formatActorColumn(row.original.actorName, row.original.actorRole, t)}
          </Text>
        ),
      },
    ],
    [t, i18n.language],
  )

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const hasActiveFilters = Object.values(activeFilters).some(Boolean) || !!debouncedQuery
  const isFetching = activityQuery.isFetching && !activityQuery.isLoading

  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text)
    setPage(1)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
    setPage(1)
  }, [])

  function handleFilterChange(id: string, value: string | string[] | undefined) {
    setActiveFilters((prev) => ({ ...prev, [id]: value }))
    setPage(1)
  }

  function handleDateChange(from: Date, to: Date) {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
  }

  async function handleExport() {
    setIsExporting(true)
    setExportError(false)
    try {
      const dateRange = `${format(dateFrom, 'd MMM yyyy')} – ${format(dateTo, 'd MMM yyyy')}`

      const activityTypeLabels = activityTypes?.map((type) =>
        type === 'new_member_joined'
          ? t('recentActivity.activityTypes.new_member_joined')
          : t(`common.activity.type.${type}` as never),
      )

      const actorLabels = actorIds
        ? actorList
            .filter((a) => actorIds.includes(a.id))
            .map((a) =>
              a.role === 'manager'
                ? `${a.name} (${t('recentActivity.detail.managerRole')})`
                : a.role === 'admin'
                  ? `${a.name} (${t('recentActivity.detail.adminRole')})`
                  : a.name,
            )
        : undefined

      const memberStatusLabels = anonymisedFilter?.map((v) =>
        t(`recentActivity.filters.${v}` as never),
      )

      const filterLabels: ExportFilterLabels = {
        dateRange,
        activityTypes: activityTypeLabels,
        actors: actorLabels,
        memberStatus: memberStatusLabels,
        search: debouncedQuery || undefined,
      }

      await exportRecentActivityCsv(
        {
          dateFrom,
          dateTo,
          memberQuery: debouncedQuery || undefined,
          activityTypes,
          actorIds,
          isAnonymised: anonymisedFilter?.length === 1
            ? anonymisedFilter[0] === 'anonymised'
            : undefined,
        },
        filterLabels,
        t,
        i18n.language,
      )
    } catch {
      // Already reported to Sentry inside exportRecentActivityCsv — this
      // catch is just for the user-facing error state.
      setExportError(true)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <AdminScreen
      title={t('adminDashboard.recentActivityTitle')}
      subtitle={t('adminDashboard.recentActivitySubtitle')}
      actions={
        <Button
          label={t('recentActivity.export')}
          loadingLabel={t('recentActivity.exporting')}
          LeftIcon={Download}
          fullWidth={false}
          loading={isExporting}
          accessibilityLabel={t('recentActivity.exportAccessibilityLabel')}
          onPress={handleExport}
        />
      }
    >
      <Card variant="dashboard" className="p-6">
        {exportError ? (
          <View className="mb-4">
            <InlineAlert message={t('recentActivity.exportError')} />
          </View>
        ) : null}
        <View className="gap-2 lg:flex-row lg:items-center lg:justify-between">
          <View className="min-w-0 flex-1 lg:max-w-[400px]">
            <TextField
              placeholder={t('recentActivity.search.placeholder')}
              accessibilityLabel={t('recentActivity.search.accessibilityLabel')}
              prefix={Search}
              suffix={searchQuery ? X : undefined}
              onSuffixPress={searchQuery ? handleSearchClear : undefined}
              suffixAccessibilityLabel={t('recentActivity.search.clearAccessibilityLabel')}
              value={searchQuery}
              onChangeText={handleSearchChange}
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>
          <View className="flex-row items-center justify-end gap-2">
            <AdminFilterDropdown
              groups={filterGroups}
              activeFilters={activeFilters}
              onChange={handleFilterChange}
              filterLabel={t('recentActivity.filters.label')}
              clearLabel={t('recentActivity.filters.clear')}
            />
            <DateRangePicker
              from={dateFrom}
              to={dateTo}
              onChange={handleDateChange}
            />
          </View>
        </View>

        <AdminTable
          table={table}
          minWidth={530}
          alignRows="top"
          isLoading={activityQuery.isLoading}
          isError={activityQuery.isError}
          isFetching={isFetching}
          hasActiveFilters={hasActiveFilters}
          onRowPress={setSelectedEntry}
          rowAccessibilityLabel={(entry) =>
            t('recentActivity.rowAccessibilityLabel', {
              activity: getActivityLabel(entry.type, t),
              member: entry.memberName,
              time: formatDateTime(entry.createdAt, t, i18n.language),
            })
          }
          renderMobileRow={(entry) => {
            const delta = formatMobileDelta(entry, t)
            return (
              <>
                <View className="flex-row items-center justify-between gap-3">
                  <View className="min-w-0 flex-1 flex-row items-center gap-2">
                    <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
                      {getActivityLabel(entry.type, t)}
                    </Text>
                    {delta ? (
                      <Text className={`shrink-0 text-xs font-medium ${delta.isNegative ? 'text-accent' : 'text-primary'}`}>
                        {delta.label}
                      </Text>
                    ) : null}
                  </View>
                  <Text className="shrink-0 text-xs font-normal text-muted" numberOfLines={1}>
                    {formatRelativeTime(entry.createdAt)}
                  </Text>
                </View>
                <View className="mt-1 flex-row items-center gap-2">
                  <Avatar name={entry.memberName} email={entry.memberEmail} size={24} />
                  <Text className="min-w-0 flex-1 text-xs font-normal text-secondary" numberOfLines={1}>
                    {entry.memberName}
                  </Text>
                </View>
              </>
            )
          }}
          onRetry={() => void activityQuery.refetch()}
          errorMessage={t('recentActivity.loadError')}
          noResultsDescription={t('recentActivity.noResults')}
          emptyIcon={Activity}
          emptyTitle={t('recentActivity.emptyTitle')}
          emptyDescription={t('recentActivity.emptyDescription')}
          page={page}
          pageCount={pageCount}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPreviousPage={() => setPage((p) => Math.max(p - 1, 1))}
          onNextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        />
      </Card>
      <RecentActivityDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </AdminScreen>
  )
}